import React from 'react';
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGripVertical, faPencilAlt, faTrash } from '@fortawesome/free-solid-svg-icons';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { v4 as uuid } from 'uuid';
import DashboardTemplate from '../../Common/DashboardTemplate';
import DrawerMenuRight from '../../Common/DrawerMenuRight';
import { shippingMethodService } from '../../../Services/ShippingMethodService';
import {
  COUNTRIES,
  CONTINENTAL_48_STATE_CODES,
  US_STATES,
} from '../../../utils/geo';

const emptyWeightTier = () => ({
  maxWeightPounds: '',
  maxWeightOunces: '',
  rate: '',
});

const emptyForm = () => ({
  id: null,
  name: '',
  description: '',
  isActive: true,
  rateType: 'ShipStation',
  carrierCode: '',
  serviceCode: '',
  packageCode: '',
  percentMarkup: 20,
  flatAdjustment: 0,
  minCharged: '',
  maxCharged: '',
  minSubtotal: '',
  maxSubtotal: '',
  allowedCountryCodes: [],
  allowedUsStateCodes: [],
  weightTiers: [emptyWeightTier()],
});

class ShippingMethods extends React.Component {
  state = {
    loading: true,
    saving: false,
    reordering: false,
    methods: [],
    carriers: [],
    services: [],
    form: emptyForm(),
    editorOpen: false,
    error: null,
  };

  componentDidMount() {
    this.loadMethods();
    shippingMethodService.getCarriers()
      .then((carriers) => this.setState({ carriers: carriers || [] }))
      .catch(() => this.setState({ error: 'Unable to load ShipStation carriers.' }));
  }

  loadMethods = () => {
    this.setState({ loading: true, error: null });
    shippingMethodService.list()
      .then((methods) => this.setState({
        methods: this.sortMethods(methods || []),
        loading: false,
      }))
      .catch((err) => this.setState({
        loading: false,
        error: err?.message || 'Failed to load shipping methods',
      }));
  };

  sortMethods = (methods) => [...(methods || [])].sort((a, b) => {
    const orderDiff = Number(a.sortOrder || 0) - Number(b.sortOrder || 0);
    if (orderDiff !== 0) return orderDiff;
    return String(a.name || '').localeCompare(String(b.name || ''), undefined, { numeric: true, sensitivity: 'base' });
  });

  openEditor = (method = null) => {
    const form = method ? {
      ...emptyForm(),
      ...method,
      rateType: method.rateType || 'ShipStation',
      minCharged: method.minCharged ?? '',
      maxCharged: method.maxCharged ?? '',
      minSubtotal: method.minSubtotal ?? '',
      maxSubtotal: method.maxSubtotal ?? '',
      allowedCountryCodes: method.allowedCountryCodes || [],
      allowedUsStateCodes: method.allowedUsStateCodes || [],
      weightTiers: (method.weightTiers && method.weightTiers.length)
        ? method.weightTiers.map((tier) => ({
          maxWeightPounds: tier.maxWeightPounds ?? '',
          maxWeightOunces: tier.maxWeightOunces ?? '',
          rate: tier.rate ?? '',
        }))
        : [emptyWeightTier()],
    } : emptyForm();
    this.setState({ editorOpen: true, form, services: [], error: null });
    if (form.carrierCode) this.loadServices(form.carrierCode);
  };

  loadServices = (carrierCode) => {
    if (!carrierCode) {
      this.setState({ services: [] });
      return;
    }
    shippingMethodService.getServices(carrierCode)
      .then((services) => this.setState({ services: services || [] }))
      .catch(() => this.setState({ services: [], error: 'Unable to load ShipStation services.' }));
  };

  setForm = (patch) => {
    this.setState((prev) => ({ form: { ...prev.form, ...patch } }));
  };

  setRateType = (rateType) => {
    const { form } = this.state;
    const patch = { rateType };
    if (rateType === 'WeightFlatRate') {
      if (Number(form.percentMarkup) === 20) patch.percentMarkup = 0;
      if (!form.weightTiers || form.weightTiers.length === 0) {
        patch.weightTiers = [emptyWeightTier()];
      }
    } else if (rateType === 'ShipStation' && Number(form.percentMarkup) === 0) {
      patch.percentMarkup = 20;
    }
    this.setForm(patch);
  };

  updateWeightTier = (index, field, value) => {
    const tiers = [...(this.state.form.weightTiers || [])];
    const next = { ...tiers[index], [field]: value };
    if (field === 'maxWeightOunces') {
      const ounces = parseInt(value, 10) || 0;
      if (ounces >= 16) {
        next.maxWeightPounds = (parseInt(next.maxWeightPounds, 10) || 0) + Math.floor(ounces / 16);
        next.maxWeightOunces = ounces % 16;
      }
    }
    tiers[index] = next;
    this.setForm({ weightTiers: tiers });
  };

  addWeightTier = () => {
    this.setForm({ weightTiers: [...(this.state.form.weightTiers || []), emptyWeightTier()] });
  };

  removeWeightTier = (index) => {
    const tiers = [...(this.state.form.weightTiers || [])];
    if (tiers.length <= 1) return;
    tiers.splice(index, 1);
    this.setForm({ weightTiers: tiers });
  };

  toggleCode = (field, code) => {
    this.setState((prev) => {
      const current = prev.form[field] || [];
      const next = current.includes(code)
        ? current.filter((c) => c !== code)
        : [...current, code];
      return { form: { ...prev.form, [field]: next } };
    });
  };

  save = () => {
    const { form } = this.state;
    const payload = {
      id: form.id || uuid(),
      name: form.name,
      description: form.description,
      isActive: !!form.isActive,
      rateType: form.rateType || 'ShipStation',
      percentMarkup: Number(form.percentMarkup || 0),
      flatAdjustment: Number(form.flatAdjustment || 0),
      minCharged: form.minCharged === '' ? null : Number(form.minCharged),
      maxCharged: form.maxCharged === '' ? null : Number(form.maxCharged),
      minSubtotal: form.minSubtotal === '' ? null : Number(form.minSubtotal),
      maxSubtotal: form.maxSubtotal === '' ? null : Number(form.maxSubtotal),
      carrierCode: form.carrierCode || '',
      serviceCode: form.serviceCode || '',
      packageCode: form.packageCode || null,
      allowedCountryCodes: form.allowedCountryCodes || [],
      allowedUsStateCodes: form.allowedUsStateCodes || [],
      weightTiers: (form.weightTiers || []).map((tier) => ({
        maxWeightPounds: Number(tier.maxWeightPounds || 0),
        maxWeightOunces: Number(tier.maxWeightOunces || 0),
        rate: Number(tier.rate || 0),
      })),
    };
    this.setState({ saving: true, error: null });
    const promise = form.id
      ? shippingMethodService.update(form.id, payload)
      : shippingMethodService.create(payload);
    promise
      .then(() => {
        this.setState({ saving: false, editorOpen: false, form: emptyForm() });
        this.loadMethods();
      })
      .catch((err) => this.setState({
        saving: false,
        error: err?.response?.data?.message || err?.message || 'Failed to save shipping method',
      }));
  };

  remove = (method) => {
    if (!window.confirm(`Delete shipping method "${method.name}"?`)) return;
    shippingMethodService.delete(method.id)
      .then(() => this.loadMethods())
      .catch((err) => this.setState({ error: err?.message || 'Failed to delete shipping method' }));
  };

  onDragEnd = (result) => {
    if (!result.destination) return;
    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;
    if (sourceIndex === destinationIndex) return;

    const previousMethods = this.state.methods;
    const methods = [...previousMethods];
    const [moved] = methods.splice(sourceIndex, 1);
    methods.splice(destinationIndex, 0, moved);
    const ordered = methods.map((method, index) => ({ ...method, sortOrder: index }));
    this.setState({ methods: ordered, reordering: true, error: null });
    shippingMethodService.reorder(ordered.map((method) => method.id))
      .then(() => this.setState({ reordering: false }))
      .catch((err) => {
        this.setState({
          methods: previousMethods,
          reordering: false,
          error: err?.response?.data?.message || err?.message || 'Failed to save shipping method order',
        });
      });
  };

  renderCheckboxGroup(title, items, selected, field, extras) {
    return (
      <div style={{ marginTop: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <Typography variant="subtitle2">{title}</Typography>
          <div>
            {extras}
            <Button size="small" onClick={() => this.setForm({ [field]: items.map((i) => i.code) })}>Select all</Button>
            <Button size="small" onClick={() => this.setForm({ [field]: [] })}>Select none</Button>
          </div>
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
          maxHeight: 260,
          overflow: 'auto',
          border: '1px solid #ddd',
          padding: 8,
          marginTop: 8,
        }}
        >
          {items.map((item) => (
            <FormControlLabel
              key={item.code}
              control={(
                <Checkbox
                  color="primary"
                  checked={selected.includes(item.code)}
                  onChange={() => this.toggleCode(field, item.code)}
                />
              )}
              label={item.name}
            />
          ))}
        </div>
      </div>
    );
  }

  renderMethodTile = (method, index) => (
    <Draggable key={method.id} draggableId={String(method.id)} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          style={{
            display: 'flex',
            alignItems: 'stretch',
            gap: 16,
            padding: '16px 18px',
            marginBottom: 12,
            backgroundColor: 'white',
            border: '1px solid #9d9d9d',
            borderRadius: 8,
            boxShadow: snapshot.isDragging
              ? '0 8px 18px rgba(0,0,0,0.18)'
              : '0 2px 4px rgba(0,0,0,0.1)',
            opacity: method.isActive ? 1 : 0.72,
            ...provided.draggableProps.style,
          }}
        >
          <div
            {...provided.dragHandleProps}
            title="Drag to reorder"
            style={{
              display: 'flex',
              alignItems: 'center',
              color: '#888',
              cursor: 'grab',
              paddingRight: 4,
            }}
          >
            <FontAwesomeIcon icon={faGripVertical} />
          </div>
          <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ fontWeight: 700, fontSize: 16 }}>{method.name}</div>
              <div style={{
                fontWeight: 600,
                fontSize: 12,
                color: method.isActive ? '#16a34a' : '#dc2626',
              }}
              >
                {method.isActive ? 'Active' : 'Inactive'}
              </div>
            </div>
            <div style={{ color: '#666', fontSize: 13, marginTop: 4 }}>{method.description || 'No description'}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 18px', marginTop: 10, fontSize: 13, color: '#444' }}>
              <span>{method.destinationSummary || 'No destinations'}</span>
              <span>{method.carrierCode || '—'} / {method.serviceCode || '—'}</span>
              <span>
                {method.rateType === 'WeightFlatRate'
                  ? `Weight (${(method.weightTiers || []).length} tier${(method.weightTiers || []).length === 1 ? '' : 's'})`
                  : 'Live ShipStation rates'}
              </span>
              <span>
                {Number(method.percentMarkup || 0)}% markup
                {Number(method.flatAdjustment || 0) !== 0 ? ` / ${Number(method.flatAdjustment).toFixed(2)}` : ''}
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <FontAwesomeIcon
              icon={faPencilAlt}
              style={{ cursor: 'pointer' }}
              title="Edit"
              onClick={() => this.openEditor(method)}
            />
            <FontAwesomeIcon
              icon={faTrash}
              style={{ cursor: 'pointer', color: '#f44336' }}
              title="Delete"
              onClick={() => this.remove(method)}
            />
          </div>
        </div>
      )}
    </Draggable>
  );

  render() {
    const {
      loading,
      saving,
      methods,
      carriers,
      services,
      form,
      editorOpen,
      error,
    } = this.state;
    const includesUs = (form.allowedCountryCodes || []).includes('US');

    return (
      <DashboardTemplate title="Shipping Methods" onSyncDatabase={this.loadMethods}>
        <div className="main-dashboard-root">
          <div className="container" style={{maxWidth: 1000}}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
              <Button
                variant="contained"
                color="primary"
                className="success-button-lg"
                onClick={() => this.openEditor()}
              >
                Add Method
              </Button>
            </div>
            {error && <Typography color="error" gutterBottom>{error}</Typography>}
            {loading ? (
              <div style={{ textAlign: 'center', padding: 40 }}><CircularProgress /></div>
            ) : methods.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#666' }}>
                No shipping methods yet. Add one to get started.
              </div>
            ) : (
              <DragDropContext onDragEnd={this.onDragEnd}>
                <Droppable droppableId="shipping-methods">
                  {(provided) => (
                    <div ref={provided.innerRef} {...provided.droppableProps}>
                      {methods.map((method, index) => this.renderMethodTile(method, index))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            )}
          </div>
        </div>

        {editorOpen && (
          <DrawerMenuRight
            title={form.id ? 'Edit shipping method' : 'New shipping method'}
            open={editorOpen}
            onClose={() => this.setState({ editorOpen: false })}
            customWidth={760}
          >
            <div style={{ padding: 20 }}>
              <TextField
                label="Name"
                value={form.name}
                onChange={(e) => this.setForm({ name: e.target.value })}
                fullWidth
                variant="outlined"
                style={{ marginBottom: 12 }}
              />
              <TextField
                label="Description"
                value={form.description}
                onChange={(e) => this.setForm({ description: e.target.value })}
                fullWidth
                variant="outlined"
                multiline
                rows={2}
                style={{ marginBottom: 12 }}
              />
              <FormControlLabel
                control={(
                  <Checkbox
                    color="primary"
                    checked={!!form.isActive}
                    onChange={(e) => this.setForm({ isActive: e.target.checked })}
                  />
                )}
                label="Active"
              />

              <Typography variant="subtitle2" style={{ marginTop: 16 }}>ShipStation mapping</Typography>
              <Typography variant="caption" style={{ display: 'block', color: '#666', marginBottom: 8 }}>
                {form.rateType === 'WeightFlatRate'
                  ? 'Customers are charged the weight-tier rates. This carrier and service are stored on the order and used with the ShipStation API when buying postage on fulfill.'
                  : 'Live checkout rates come from this carrier and service. The same mapping is used when buying postage on fulfill.'}
              </Typography>
              <FormControl fullWidth variant="outlined" style={{ marginTop: 8, marginBottom: 12 }}>
                <InputLabel>Carrier</InputLabel>
                <Select
                  value={form.carrierCode}
                  label="Carrier"
                  onChange={(e) => {
                    this.setForm({ carrierCode: e.target.value, serviceCode: '' });
                    this.loadServices(e.target.value);
                  }}
                >
                  {(carriers || []).map((carrier) => (
                    <MenuItem key={carrier.code} value={carrier.code}>{carrier.name} ({carrier.code})</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth variant="outlined" style={{ marginBottom: 12 }}>
                <InputLabel>Service</InputLabel>
                <Select
                  value={form.serviceCode}
                  label="Service"
                  onChange={(e) => this.setForm({ serviceCode: e.target.value })}
                >
                  {(services || []).map((service) => (
                    <MenuItem key={service.code} value={service.code}>{service.name} ({service.code})</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Package code (optional)"
                value={form.packageCode}
                onChange={(e) => this.setForm({ packageCode: e.target.value })}
                fullWidth
                variant="outlined"
                style={{ marginBottom: 12 }}
              />

              <Typography variant="subtitle2" style={{ marginTop: 16 }}>Rate logic</Typography>
              <FormControl fullWidth variant="outlined" style={{ marginTop: 8, marginBottom: 12 }}>
                <InputLabel>How this method is priced</InputLabel>
                <Select
                  value={form.rateType || 'ShipStation'}
                  label="How this method is priced"
                  onChange={(e) => this.setRateType(e.target.value)}
                >
                  <MenuItem value="ShipStation">Live ShipStation rates</MenuItem>
                  <MenuItem value="WeightFlatRate">Flat rates by cart weight</MenuItem>
                </Select>
              </FormControl>

              {form.rateType === 'WeightFlatRate' && (
                <div style={{ marginBottom: 16 }}>
                  <Typography variant="subtitle2">Customer weight-tier rates</Typography>
                  {(form.weightTiers || []).map((tier, index) => (
                    <div
                      key={index}
                      style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 8, marginBottom: 8, alignItems: 'center' }}
                    >
                      <TextField
                        label="Max pounds"
                        type="number"
                        variant="outlined"
                        value={tier.maxWeightPounds}
                        onChange={(e) => this.updateWeightTier(index, 'maxWeightPounds', e.target.value)}
                        inputProps={{ min: 0 }}
                      />
                      <TextField
                        label="Max ounces"
                        type="number"
                        variant="outlined"
                        value={tier.maxWeightOunces}
                        onChange={(e) => this.updateWeightTier(index, 'maxWeightOunces', e.target.value)}
                        inputProps={{ min: 0, max: 15 }}
                      />
                      <TextField
                        label="Rate ($)"
                        type="number"
                        variant="outlined"
                        value={tier.rate}
                        onChange={(e) => this.updateWeightTier(index, 'rate', e.target.value)}
                        inputProps={{ min: 0, step: 0.01 }}
                      />
                      <Button
                        onClick={() => this.removeWeightTier(index)}
                        disabled={(form.weightTiers || []).length <= 1}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                  <Button size="small" color="primary" onClick={this.addWeightTier}>Add threshold</Button>
                </div>
              )}

              <Typography variant="subtitle2" style={{ marginTop: 8 }}>Pricing</Typography>
              <Typography variant="caption" style={{ display: 'block', color: '#666', marginTop: 4 }}>
                {form.rateType === 'WeightFlatRate'
                  ? 'Markup and min/max charged still apply to the matched weight-tier rate. Leave markup at 0 to charge the tier rates as entered.'
                  : 'Charged amount = ShipStation rate × (1 + percent markup / 100) + flat adjustment, then clamped to min/max charged.'}
              </Typography>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 8 }}>
                <TextField label="Percent markup" type="number" variant="outlined" value={form.percentMarkup} onChange={(e) => this.setForm({ percentMarkup: e.target.value })} />
                <TextField label="Flat adjustment" type="number" variant="outlined" value={form.flatAdjustment} onChange={(e) => this.setForm({ flatAdjustment: e.target.value })} />
                <TextField label="Min charged" type="number" variant="outlined" value={form.minCharged} onChange={(e) => this.setForm({ minCharged: e.target.value })} />
                <TextField label="Max charged" type="number" variant="outlined" value={form.maxCharged} onChange={(e) => this.setForm({ maxCharged: e.target.value })} helperText="Use 0 for free shipping" />
                <TextField label="Min order subtotal" type="number" variant="outlined" value={form.minSubtotal} onChange={(e) => this.setForm({ minSubtotal: e.target.value })} />
                <TextField label="Max order subtotal" type="number" variant="outlined" value={form.maxSubtotal} onChange={(e) => this.setForm({ maxSubtotal: e.target.value })} />
              </div>

              {this.renderCheckboxGroup('Countries', COUNTRIES, form.allowedCountryCodes || [], 'allowedCountryCodes')}
              {includesUs && this.renderCheckboxGroup(
                'United States destinations',
                US_STATES,
                form.allowedUsStateCodes || [],
                'allowedUsStateCodes',
                <Button size="small" onClick={() => this.setForm({ allowedUsStateCodes: [...CONTINENTAL_48_STATE_CODES] })}>Continental 48</Button>
              )}

              <div style={{ marginTop: 24 }}>
                <Button variant="contained" color="primary" className="success-button-lg" disabled={saving} onClick={this.save}>
                  {saving ? 'Saving…' : 'Save'}
                </Button>
                <Button onClick={() => this.setState({ editorOpen: false })} style={{ marginLeft: 8 }}>Cancel</Button>
              </div>
            </div>
          </DrawerMenuRight>
        )}
      </DashboardTemplate>
    );
  }
}

export default ShippingMethods;
